import { useCallback, useEffect, useState } from 'react';
import { GlobalActions } from '../../model/global-actions';
import { useAppDispatch, useAppSelector } from '../redux';
import { messagesForReferralCodeError } from '../../store/features/referral-code/referral-code-utils';
import { useIntl } from 'react-intl';
import {
  checkReferralCode,
  resetReferralCodeState,
  updateReferralCodeState,
} from '../../store/features/referral-code/referral-code-slice';
import { ReferralCodeRequestParams } from '../../store/features/referral-code/referral-code-types';
import { ApiRequestStatus } from '../../model';
import appRoutes from '../../routing/app-routes';
import { useAppNavigation } from '../../utils/navigation.utils';

export interface ReferralCodeLogicCallbacks {
  referralCode: string;
  setReferralCode: (referralCode: string | undefined) => void;
  handleSubmit: () => void;
  checkReferralCodeRequestStatus: ApiRequestStatus;
}

export const useReferralCodeLogic = (
  actions: GlobalActions
): ReferralCodeLogicCallbacks => {
  const { appNavigate } = useAppNavigation();
  const { showToast, closeToast } = actions;
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const {
    referralCodeError,
    checkReferralCodeRequestStatus,
    workflowName,
    sessionId,
  } = useAppSelector((state) => state.features.referralCode);

  /** Error management */
  useEffect(() => {
    if (referralCodeError) {
      const errorMessages = messagesForReferralCodeError(referralCodeError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(resetReferralCodeState());
    }
  }, [closeToast, dispatch, formatMessage, referralCodeError, showToast]);

  /** Form management */
  const [referralCode, setReferralCode] = useState('');

  const handleSubmit = useCallback(() => {
    if (referralCode === '') {
      dispatch(updateReferralCodeState('FILL_ALL_FIELDS'));
    } else {
      dispatch(
        checkReferralCode({
          referralCode: referralCode,
        } as ReferralCodeRequestParams)
      );
    }
  }, [dispatch, referralCode]);

  useEffect(() => {
    if (checkReferralCodeRequestStatus === 'succeeded') {
      const path = `${appRoutes.InvydioEmbeddedSession}?workflowName=${workflowName}&sessionId=${sessionId}`;
      appNavigate(path, { replace: true });
    }
  }, [checkReferralCodeRequestStatus, appNavigate, sessionId, workflowName]);

  return {
    referralCode,
    setReferralCode,
    handleSubmit,
    checkReferralCodeRequestStatus,
  };
};
